/**
 * App-wide undo/redo shortcuts for the adjacency map (WQ-340, decision D2).
 *
 * Ctrl/Cmd+Z → undo; Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z → redo, in every view.
 * Ignored inside text fields (native text undo, WQ-339) and while a modal
 * dialog is open (it handles its own keys; modals carry aria-modal="true").
 */

import { useEffect } from 'react';

/** Keys typed into text fields belong to the field. */
export function isTextTarget(target: EventTarget | null): boolean {
  const t = target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}

/** True while a modal dialog is open. */
export function dialogOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

export function useUndoShortcuts(undo: () => Promise<void>, redo: () => Promise<void>): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || isTextTarget(e.target) || dialogOpen()) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        void undo();
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        void redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);
}
